import { createNodeFromTemplate } from '../constants/csmlNodeTemplates';

// An unset URL must stay empty so the node shows its missing-file issue,
// instead of being filled with the placeholder text.
const MEDIA_TYPES = ['file', 'video', 'audio'];

function extractCallArgument(line, methodName) {
  const token = `.${methodName}(`;
  const start = line.toLowerCase().indexOf(token);
  if (start < 0) return '';
  const open = start + token.length;
  if (line[open] !== '{') return '';
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let index = open; index < line.length; index += 1) {
    const char = line[index];
    if (inString) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === '"') inString = false;
    } else if (char === '"') {
      inString = true;
    } else if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) return line.slice(open, index + 1);
    }
  }
  return '';
}

function parseHttpCall(line) {
  const urlMatch = line.match(/HTTP\(["']([^"']*)["']\)/);
  const methodMatch = line.match(/\.(get|post|put|patch|delete)\s*\(/i);
  const method = methodMatch ? methodMatch[1].toLowerCase() : 'get';
  const sendsBody = ['post', 'put', 'patch'].includes(method);
  return {
    apiUrl: urlMatch ? urlMatch[1] : 'https://api.example.com',
    apiMethod: method.toUpperCase(),
    apiHeaders: extractCallArgument(line, 'set'),
    apiBody: sendsBody ? extractCallArgument(line, method) : '',
  };
}

const STEP_NAME = '[A-Za-z0-9_-]+';

function unescapeCsmlString(value) {
  return String(value ?? '')
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"');
}

// Question(...) and HTTP.post(...) are written across several lines. A step
// header is still one line; only a `say`/`do` or an is_error block that is
// still open at the end of the line continues onto the next.
function depthDelta(line) {
  let delta = 0;
  let inString = false;
  let escaped = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (inString) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === '"') inString = false;
    } else if (char === '"') {
      inString = true;
    } else if (char === '(' || char === '[' || char === '{') {
      delta += 1;
    } else if (char === ')' || char === ']' || char === '}') {
      delta -= 1;
    }
  }
  return delta;
}

function isEventMatchTail(line) {
  return (
    /^(say|hold)\b/.test(line) ||
    new RegExp(`^goto\\s+${STEP_NAME}\\s*$`).test(line)
  );
}

// `if (event match name)` is often followed by say / hold / goto on the next
// lines, with no braces. Those lines belong to the branch. A `say` left on
// its own would replace the step's Question.
function joinStatements(lines) {
  const statements = [];
  let buffer = [];
  let depth = 0;
  let tailMode = false;

  const flush = () => {
    if (!buffer.length) return;
    statements.push(buffer.join('\n'));
    buffer = [];
    depth = 0;
    tailMode = false;
  };

  lines.forEach(line => {
    if (tailMode) {
      if (line.startsWith('//')) return;
      if (isEventMatchTail(line)) {
        buffer.push(line);
        if (/^goto\b/.test(line)) flush();
        return;
      }
      flush();
    }
    if (!buffer.length && line.startsWith('//')) return;
    const delta = depthDelta(line);
    const opens =
      /^(say|do)\b/.test(line) ||
      /^if\s*\(\s*response\.is_error\s*\(/.test(line) ||
      (/^if\s*\(\s*event\s+match\b/.test(line) && delta > 0);
    const openEventMatch =
      /^if\s*\(\s*event\s+match\b/.test(line) &&
      delta === 0 &&
      !/\bgoto\b/.test(line);
    if (!buffer.length && openEventMatch) {
      buffer = [line];
      tailMode = true;
      return;
    }
    if (!buffer.length && opens && delta > 0) {
      buffer = [line];
      depth = delta;
      return;
    }
    if (buffer.length) {
      buffer.push(line);
      depth += delta;
      if (depth <= 0) flush();
      return;
    }
    statements.push(line);
  });
  flush();
  return statements;
}

function splitCallArgs(source, openIndex) {
  const args = [];
  let current = '';
  let depth = 1;
  let inString = false;
  let escaped = false;
  for (let index = openIndex; index < source.length; index += 1) {
    const char = source[index];
    if (inString) {
      current += char;
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === '"') inString = false;
    } else if (char === '"') {
      inString = true;
      current += char;
    } else if (char === '(' || char === '[' || char === '{') {
      depth += 1;
      current += char;
    } else if (char === ')' || char === ']' || char === '}') {
      depth -= 1;
      if (depth === 0) {
        if (current.trim()) args.push(current.trim());
        return args;
      }
      current += char;
    } else if (char === ',' && depth === 1) {
      args.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  return args;
}

function parseHttpPostCall(statement) {
  const start = statement.indexOf('HTTP.post(');
  if (start < 0) return null;
  const args = splitCallArgs(statement, start + 'HTTP.post('.length);
  if (args.length < 3) return null;
  const url = args[0].match(/^["']([^"']*)["']$/);
  if (!url) return null;
  return {
    callStyle: 'http.post',
    apiUrl: url[1],
    apiMethod: 'POST',
    apiHeaders: args[1],
    apiBody: args[2],
    parseJson: true,
    responseField: 'response',
    errorMessage: '',
  };
}

function parseAssignCall(text) {
  const typeMatch = text.match(
    /type\s*=\s*["'](available_agent|team|agent)["']/
  );
  const idMatch = text.match(/(?:^|[(,]\s*)id\s*=\s*["']?(\d+)/);
  const teamIdMatch = text.match(/team_id\s*=\s*["']?(\d+)/);
  return {
    assignType: typeMatch ? typeMatch[1] : 'team',
    id: idMatch ? idMatch[1] : '',
    teamId: teamIdMatch ? teamIdMatch[1] : '',
    available: /availability\s*=\s*["']available["']/.test(text),
  };
}

function requestsAvailableAgent(parsed) {
  return (
    parsed.assignType === 'available_agent' ||
    (parsed.assignType === 'agent' &&
      parsed.available &&
      Boolean(parsed.teamId || parsed.id))
  );
}

function parseBranchTail(tail) {
  const parts = String(tail || '')
    .split(/\bsay\s+/i)
    .map(part => part.trim())
    .filter(Boolean);
  const assigns = [];
  let message = '';
  parts.forEach(part => {
    if (/^Assign\s*\(/i.test(part)) {
      assigns.push(parseAssignCall(part));
      return;
    }
    const quoted = part.match(/^"((?:\\.|[^"\\])*)"/);
    if (quoted) message = unescapeCsmlString(quoted[1]);
  });
  const team = assigns.find(item => item.assignType === 'team');
  const availableAgent = assigns.find(requestsAvailableAgent);
  return {
    message,
    teamId: team?.id || availableAgent?.teamId || availableAgent?.id || '',
    assignAvailableAgent: Boolean(availableAgent),
    assignType:
      team || availableAgent ? 'team' : assigns[0]?.assignType || 'team',
    agentId:
      !team && !availableAgent && assigns[0]?.assignType === 'agent'
        ? assigns[0].id || assigns[0].teamId
        : '',
    hasAssign: assigns.length > 0,
  };
}

function parseEventMatch(statement) {
  const compact = statement.replace(/\s+/g, ' ').trim();
  const head = compact.match(
    new RegExp(
      `^if\\s*\\(\\s*event\\s+match\\s+(?:"((?:\\\\.|[^"\\\\])*)"|(${STEP_NAME}))\\s*\\)\\s*(.*)$`
    )
  );
  if (!head) return null;
  let rest = head[3].trim();
  if (rest.startsWith('{')) rest = rest.slice(1).trim();
  rest = rest.replace(/\}\s*$/, '').trim();
  const hold = /\bhold\b/.test(rest);
  rest = rest
    .replace(/\bhold\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const gotoMatch = rest.match(new RegExp(`\\bgoto\\s+(${STEP_NAME})\\s*$`));
  const targetLabel = gotoMatch ? gotoMatch[1] : null;
  if (gotoMatch) rest = rest.slice(0, gotoMatch.index).trim();
  return {
    matchText: head[1] != null ? unescapeCsmlString(head[1]) : null,
    matchVar: head[2] || null,
    hold,
    targetLabel,
    tail: rest,
  };
}

function parseErrorBranch(statement) {
  if (!/response\.is_error\s*\(/.test(statement)) return null;
  const flat = statement.replace(/\s+/g, ' ');
  const error = flat.match(/say\s+"((?:\\.|[^"\\])*)"/);
  const field = flat.match(/\{\{\s*data\.([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/);
  return {
    errorMessage: error ? unescapeCsmlString(error[1]) : '',
    responseField: field ? field[1] : 'response',
    parseJson: /json\.parse\s*\(/.test(statement),
  };
}

function buttonIndexFor(matchText, matchVar, buttonList) {
  if (matchVar) {
    const byName = buttonList.findIndex(button => button.varName === matchVar);
    if (byName >= 0) return byName;
    const trailing = matchVar.match(/_(\d+)$/);
    if (trailing && buttonList[Number(trailing[1])]) {
      return Number(trailing[1]);
    }
  }
  if (matchText != null) {
    const byLabel = buttonList.findIndex(
      button =>
        button.label === matchText ||
        (button.payload && button.payload === matchText)
    );
    if (byLabel >= 0) return byLabel;
  }
  return null;
}

/**
 * Best-effort CSML script → visual graph importer.
 * Ported from builder4.html buildFromScript.
 */

function parseNodeDefs(script) {
  const lines = script.split('\n');
  const nodeDefs = [];
  let currentDef = null;

  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;
    if (trimmed.match(new RegExp(`^${STEP_NAME}:$`))) {
      if (currentDef) nodeDefs.push(currentDef);
      currentDef = { label: trimmed.replace(':', '').trim(), lines: [] };
      return;
    }
    if (currentDef) currentDef.lines.push(trimmed);
  });
  if (currentDef) nodeDefs.push(currentDef);
  return nodeDefs;
}

function decimalHourToClock(value) {
  const hour = Number(value);
  if (!Number.isFinite(hour)) return '';
  const whole = Math.trunc(hour);
  const minutes = Math.round((hour - whole) * 60);
  return `${String(whole).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function restoreOutsideMessage(spoken, start, end, timezone) {
  let message = spoken;
  if (timezone) message = message.split(timezone).join('{timezone}');
  if (end) message = message.split(end).join('{end}');
  if (start) message = message.split(start).join('{start}');
  return message;
}

// Generated working-hours steps are ordinary CSML (Time.now, an if/else),
// not a component call. Recognize that exact block so a save does not drop
// the schedule or the open/closed branches.
function parseWorkingHoursBlock(lines) {
  const code = lines.filter(line => line && !line.startsWith('//'));
  if (!code.length && lines.includes('// Working Hours node is disabled')) {
    return {
      content: 'Check if within working hours',
      props: { enableWorkingHours: false },
      gotos: [],
    };
  }
  if (code.length < 8) return null;

  const timezone = code[0].match(/^do now = Time\.now\("([^"]*)"\)$/);
  const startHour = code[3].match(/^do startHour = (-?\d+(?:\.\d+)?)$/);
  const endHour = code[4].match(/^do endHour = (-?\d+(?:\.\d+)?)$/);
  const days = code[5].match(
    /^do isWorkingDay = \[((?:"[^"]*"(?:,\s*"[^"]*")*)?)\]\.includes\(day\)$/
  );
  if (
    !timezone ||
    code[1] !== 'do day = now.day_of_week()' ||
    code[2] !== 'do hour = now.hour + now.minute/60' ||
    !startHour ||
    !endHour ||
    !days ||
    code[6] !== 'if (isWorkingDay && hour >= startHour && hour < endHour) {'
  ) {
    return null;
  }

  const workingStart = decimalHourToClock(startHour[1]);
  const workingEnd = decimalHourToClock(endHour[1]);
  const gotos = [];
  let index = 7;
  if (code[index]?.startsWith('goto ')) {
    const target = code[index].match(new RegExp(`^goto (${STEP_NAME})$`));
    if (!target) return null;
    gotos.push({ targetLabel: target[1], portIdx: 0 });
    index += 1;
  }
  if (code[index] !== '} else {') return null;
  index += 1;
  const spoken = code[index]?.match(/^say "((?:\\.|[^"\\])*)"$/);
  if (!spoken) return null;
  index += 1;
  if (code[index]?.startsWith('goto ')) {
    const target = code[index].match(new RegExp(`^goto (${STEP_NAME})$`));
    if (!target) return null;
    gotos.push({ targetLabel: target[1], portIdx: 1 });
    index += 1;
  }
  if (code[index] !== '}' || index !== code.length - 1) return null;

  return {
    content: 'Check if within working hours',
    props: {
      enableWorkingHours: true,
      timezone: timezone[1],
      workingDays: [...days[1].matchAll(/"([^"]*)"/g)].map(match => match[1]),
      workingStart,
      workingEnd,
      outsideMessage: restoreOutsideMessage(
        unescapeCsmlString(spoken[1]),
        workingStart,
        workingEnd,
        timezone[1]
      ),
    },
    gotos,
  };
}

function parseButtonsFromSay(sayContent, defaultButtons) {
  const btnMatches = sayContent.match(/Button\(["']([^"']*)["']/g);
  if (!btnMatches) return defaultButtons;
  return btnMatches.map(b => {
    const labelMatch = b.match(/["']([^"']*)["']/);
    const payloadMatch = b.match(/payload=["']([^"']*)["']/);
    return {
      label: labelMatch ? labelMatch[1] : 'Button',
      payload: payloadMatch ? payloadMatch[1] : '',
    };
  });
}

function parseOptionsFromSay(sayContent, defaults) {
  const optMatches = sayContent.match(/Button\(["']([^"']*)["']/g);
  if (!optMatches) return defaults;
  return optMatches.map(b => {
    const labelMatch = b.match(/["']([^"']*)["']/);
    const payloadMatch = b.match(/payload=["']([^"']*)["']/);
    return {
      label: labelMatch ? labelMatch[1] : 'Option',
      payload: payloadMatch
        ? payloadMatch[1]
        : `opt${Math.random().toString(36).slice(2, 6)}`,
    };
  });
}

/**
 * @param {string} script
 * @returns {{ nodes: Array, connections: Array, nextId: number } | null}
 */
export function buildFromScript(script) {
  if (!script || !String(script).trim()) {
    return null;
  }

  let nodes = [];
  const connections = [];
  let nextId = 1;
  let lossy = false;

  const place = (partial, atX, atY) => {
    const id = nextId;
    nextId += 1;
    const node = {
      id,
      label:
        partial.type === 'start'
          ? 'Start'
          : partial.type.charAt(0).toUpperCase() + partial.type.slice(1),
      x: atX,
      y: atY,
      content: '',
      ...partial,
    };
    nodes.push(node);
    return node;
  };

  const nodeDefs = parseNodeDefs(script);
  const nodeMap = {};
  const definedLabels = new Set();
  let xOffset = 0;

  nodeDefs.forEach(def => {
    const label = def.label;
    // Two steps cannot share a name. A later goto would have to pick one
    // and the other menu would disappear on save.
    if (definedLabels.has(label)) lossy = true;
    definedLabels.add(label);
    let type = 'say';
    let content = '';
    let buttons = [];
    let options = [];
    let cards = [];
    // Every goto this node's block emits, with enough context (which button/
    // option/rule it belongs to) to resolve the right port below - a plain
    // list here (instead of the single last-goto-wins scalar this replaced)
    // is what makes multi-branch nodes survive a save/reopen round trip.
    const gotoEntries = [];
    let apiRuleGotoCount = 0;
    const extraProps = {};
    let hasHold = false;
    const responseRules = [];
    let isApi = false;
    // `do <name> = Button("label"[, payload="..."])` declarations, in the
    // order they appear - question/quickreply/radio/multiselect/checkbox/
    // dropdown all declare their buttons this way before referencing them
    // by name in the `say` line, so the `say` line alone never carries the
    // real label/payload.
    const declaredChoices = [];
    // `do card<i>_btn<j> = Button(...)` declarations, keyed by card index.
    const cardButtonDecls = {};
    // `do card<i> = Card(...)` declarations, keyed by card index - the
    // `say Carousel(cards=[card0,card1])` line only references these by
    // name, so title/subtitle/image_url have to come from these lines.
    const cardDecls = {};
    const keywordRules = [];
    let eventMemory = '';
    let sawHoldBeforeBody = false;

    const workingHours = parseWorkingHoursBlock(def.lines);
    if (workingHours) {
      type = 'workinghours';
      content = workingHours.content;
      Object.assign(extraProps, workingHours.props);
      workingHours.gotos.forEach(entry => gotoEntries.push(entry));
    }
    const statements = workingHours ? [] : joinStatements(def.lines);
    statements.forEach(line => {
      if (line.startsWith('//')) return;

      const eventMatch = parseEventMatch(line);
      if (eventMatch) {
        const buttonList = buttons.length ? buttons : declaredChoices;
        const buttonIndex = buttonIndexFor(
          eventMatch.matchText,
          eventMatch.matchVar,
          buttonList
        );
        const branch = parseBranchTail(eventMatch.tail);
        const inserts = [];
        if (branch.hasAssign) {
          inserts.push({
            type: 'assign',
            assignType: branch.assignType,
            teamId: branch.teamId,
            agentId: branch.agentId,
            assignAvailableAgent: branch.assignAvailableAgent,
            content: branch.message,
          });
        } else if (branch.message) {
          inserts.push({ type: 'say', content: branch.message });
        }
        if (eventMatch.hold) inserts.push({ type: 'hold' });
        const entry = {
          targetLabel: eventMatch.targetLabel,
          matchVar: eventMatch.matchVar,
          inserts,
        };
        if (buttonIndex != null) {
          entry.buttonIndex = buttonIndex;
        } else if (buttonList.length) {
          // A menu step whose match is neither a button variable nor a
          // button label cannot be wired. Saving would drop that branch.
          lossy = true;
        } else {
          const match = eventMatch.matchText || eventMatch.matchVar;
          keywordRules.push({ match });
          entry.keywordIndex = keywordRules.length;
        }
        gotoEntries.push(entry);
        return;
      }

      if (isApi && line.includes('if (response.') && line.includes('goto')) {
        const targetMatch = line.match(new RegExp(`goto\\s+(${STEP_NAME})`));
        apiRuleGotoCount += 1;
        if (targetMatch) {
          gotoEntries.push({
            targetLabel: targetMatch[1],
            apiRuleOrder: apiRuleGotoCount,
          });
        }
        // Falls through so the rule's field/operator/value is still parsed
        // by the block further down.
      } else if (line.includes('goto') && !line.includes('if (')) {
        const match = line.match(new RegExp(`goto\\s+(${STEP_NAME})`));
        if (match) gotoEntries.push({ targetLabel: match[1] });
        return;
      }

      if (line.trim() === 'hold') {
        if (!isApi && type === 'say' && !String(content).trim()) {
          sawHoldBeforeBody = true;
        }
        hasHold = true;
        return;
      }
      const remembered = line.match(
        /^(?:do|remember)\s+([A-Za-z_][A-Za-z0-9_]*)\s*=\s*event\s*$/
      );
      if (remembered) {
        if (line.startsWith('remember')) eventMemory = remembered[1];
        return;
      }
      if (/response\.is_error\s*\(/.test(line)) {
        const branch = parseErrorBranch(line);
        if (branch) {
          extraProps.errorMessage = branch.errorMessage;
          extraProps.responseField = branch.responseField;
          extraProps.parseJson = branch.parseJson;
          // Fluent HTTP().post().send() plus is_error/json.parse is the same
          // loop as HTTP.post. Keep the error say and the parsed field.
          if (branch.parseJson) extraProps.callStyle = 'http.post';
        }
        return;
      }
      if (line.includes('HTTP.post(')) {
        const posted = parseHttpPostCall(line);
        if (!posted) {
          lossy = true;
          return;
        }
        type = 'api';
        isApi = true;
        content = '';
        Object.assign(extraProps, posted);
        if (eventMemory) extraProps.eventMemory = eventMemory;
        return;
      }
      const cardBtnDecl = line.match(
        /^do\s+card(\d+)_btn(\d+)\s*=\s*Button\(["']([^"']*)["'](?:,\s*payload=["']([^"']*)["'])?\)/
      );
      if (cardBtnDecl) {
        const cardIndex = Number(cardBtnDecl[1]);
        if (!cardButtonDecls[cardIndex]) cardButtonDecls[cardIndex] = [];
        cardButtonDecls[cardIndex].push({
          label: cardBtnDecl[3],
          payload: cardBtnDecl[4] || '',
        });
        return;
      }
      const cardDecl = line.match(/^do\s+card(\d+)\s*=\s*Card\(/);
      if (cardDecl) {
        const cardIndex = Number(cardDecl[1]);
        const titleMatch = line.match(/Card\(["']([^"']*)["']/);
        const subMatch = line.match(/subtitle=["']([^"']*)["']/);
        const imgMatch = line.match(/image_url=["']([^"']*)["']/);
        cardDecls[cardIndex] = {
          title: titleMatch ? titleMatch[1] : 'Card',
          subtitle: subMatch ? subMatch[1] : '',
          image_url: imgMatch ? imgMatch[1] : 'https://via.placeholder.com/150',
        };
        return;
      }
      if (line.startsWith('say ')) {
        let sayContent = line.substring(4).trim();

        // Assignment is the Assign() component (see useCsmlGenerator), but
        // bots saved while it was briefly emitted as a raw payload object
        // still carry that form, so keep reading it too.
        const assignObject = sayContent.match(
          /"content_type"\s*:\s*"assign"[\s\S]*?"type"\s*:\s*"(team|agent)"[\s\S]*?"id"\s*:\s*"?(\d+)"?/
        );
        if (assignObject) {
          type = 'assign';
          extraProps.assignType = assignObject[1];
          if (assignObject[1] === 'agent') extraProps.agentId = assignObject[2];
          else extraProps.teamId = assignObject[2];
          return;
        }

        if (sayContent.includes('Assign(')) {
          const parsed = parseAssignCall(sayContent);
          // A handoff says the team assign, then an available-agent assign.
          // Older scripts name the team via team_id; generated scripts pass
          // that same team as id on type available_agent. Either way the
          // second call must not replace the team target.
          if (
            requestsAvailableAgent(parsed) &&
            (type === 'assign' || parsed.assignType === 'available_agent')
          ) {
            if (type !== 'assign') {
              type = 'assign';
              extraProps.assignType = 'team';
            }
            extraProps.assignAvailableAgent = true;
            if (!extraProps.teamId) {
              extraProps.teamId = parsed.teamId || parsed.id;
            }
            return;
          }
          type = 'assign';
          extraProps.assignType = parsed.assignType;
          if (parsed.assignType === 'agent') {
            extraProps.agentId = parsed.id || parsed.teamId || '';
          } else {
            extraProps.teamId = parsed.id || '';
          }
          return;
        }

        if (sayContent.startsWith('Question(')) {
          type = 'question';
          const titleMatch = sayContent.match(/["']([^"']*)["']/);
          if (titleMatch) content = titleMatch[1];
          if (/button_type\s*=\s*["']quick_reply["']/.test(sayContent)) {
            extraProps.buttonType = 'quick_reply';
          }
          buttons = declaredChoices.length
            ? declaredChoices
            : parseButtonsFromSay(sayContent, [
                { label: 'Option 1', payload: 'btn1' },
                { label: 'Option 2', payload: 'btn2' },
              ]);
        } else if (sayContent.startsWith('QuickReply(')) {
          type = 'quickreply';
          const titleMatch = sayContent.match(/["']([^"']*)["']/);
          if (titleMatch) content = titleMatch[1];
          buttons = declaredChoices.length
            ? declaredChoices
            : parseButtonsFromSay(sayContent, [
                { label: 'Yes', payload: 'yes' },
                { label: 'No', payload: 'no' },
              ]);
        } else if (sayContent.startsWith('Image(')) {
          type = 'image';
          const match = sayContent.match(/["']([^"']*)["']/);
          content = match ? match[1] : 'https://example.com/image.jpg';
        } else if (sayContent.startsWith('Video(')) {
          type = 'video';
          const match = sayContent.match(/["']([^"']*)["']/);
          content = match ? match[1] : '';
        } else if (sayContent.startsWith('Audio(')) {
          type = 'audio';
          const match = sayContent.match(/["']([^"']*)["']/);
          content = match ? match[1] : '';
        } else if (sayContent.startsWith('File(')) {
          type = 'file';
          const match = sayContent.match(/["']([^"']*)["']/);
          content = match ? match[1] : '';
        } else if (sayContent.startsWith('Url(')) {
          type = 'url';
          const match = sayContent.match(/["']([^"']*)["']/);
          content = match ? match[1] : 'https://example.com';
          const textMatch = sayContent.match(/text=["']([^"']*)["']/);
          extraProps.urlText = textMatch ? textMatch[1] : 'Visit Link';
          extraProps.hideUrl = sayContent.includes('hide_url=true');
        } else if (sayContent.startsWith('Typing(')) {
          type = 'typing';
          const match = sayContent.match(/(\d+)/);
          extraProps.duration = match ? parseInt(match[1], 10) : 1000;
          content = 'Typing...';
        } else if (sayContent.startsWith('Carousel(')) {
          type = 'carousel';
          content = 'Product carousel';
          extraProps.autoplay = sayContent.includes('autoplay=true');
          // The say line only lists card variable names (cards=[card0,
          // card1]); their actual content comes from the do card<i> = ...
          // declarations collected earlier in this pass.
          const cardIndexes = Object.keys(cardDecls)
            .map(Number)
            .sort((a, b) => a - b);
          cards = cardIndexes.map(i => ({
            ...cardDecls[i],
            buttons: cardButtonDecls[i] || [],
          }));
          if (cards.length === 0) {
            cards = [
              {
                title: 'Card 1',
                subtitle: 'Sub',
                image_url: 'https://via.placeholder.com/150',
                buttons: [{ label: 'Action', payload: 'act' }],
              },
            ];
          }
        } else if (sayContent.startsWith('Calendar(')) {
          type = 'calendar';
          content = 'Pick a date:';
          const modeMatch = sayContent.match(/input_mode=["']([^"']*)["']/);
          extraProps.mode = modeMatch ? modeMatch[1] : 'single';
          const minMatch = sayContent.match(/min_date=["']([^"']*)["']/);
          if (minMatch) extraProps.min_date = minMatch[1];
          const maxMatch = sayContent.match(/max_date=["']([^"']*)["']/);
          if (maxMatch) extraProps.max_date = maxMatch[1];
        } else if (sayContent.startsWith('Input(')) {
          type = 'input';
          const titleMatch = sayContent.match(/["']([^"']*)["']/);
          content = titleMatch ? titleMatch[1] : 'Enter value:';
          const typeMatch = sayContent.match(/type=["']([^"']*)["']/);
          extraProps.inputType = typeMatch ? typeMatch[1] : 'text';
          const placeMatch = sayContent.match(/placeholder=["']([^"']*)["']/);
          extraProps.placeholder = placeMatch ? placeMatch[1] : 'Type...';
          const descMatch = sayContent.match(/description=["']([^"']*)["']/);
          if (descMatch) extraProps.description = descMatch[1];
          const reqMatch = sayContent.match(/required=(\w+)/);
          if (reqMatch) extraProps.required = reqMatch[1] === 'true';
          const minLenMatch = sayContent.match(/minlength=(\d+)/);
          if (minLenMatch) {
            extraProps.minlength = parseInt(minLenMatch[1], 10);
          }
          const maxLenMatch = sayContent.match(/maxlength=(\d+)/);
          if (maxLenMatch) {
            extraProps.maxlength = parseInt(maxLenMatch[1], 10);
          }
          const subMatch = sayContent.match(/submit_label=["']([^"']*)["']/);
          if (subMatch) extraProps.submit_label = subMatch[1];
        } else if (sayContent.startsWith('Radio(')) {
          type = 'radio';
          const titleMatch = sayContent.match(/title=["']([^"']*)["']/);
          content = titleMatch ? titleMatch[1] : 'Select one:';
          options = declaredChoices.length
            ? declaredChoices
            : parseOptionsFromSay(sayContent, [
                { label: 'Choice 1', payload: 'c1' },
                { label: 'Choice 2', payload: 'c2' },
              ]);
        } else if (sayContent.startsWith('Multiselect(')) {
          type = 'multiselect';
          const titleMatch = sayContent.match(/title=["']([^"']*)["']/);
          content = titleMatch ? titleMatch[1] : 'Select all that apply:';
          options = declaredChoices.length
            ? declaredChoices
            : parseOptionsFromSay(sayContent, [
                { label: 'Option A', payload: 'a' },
                { label: 'Option B', payload: 'b' },
              ]);
        } else if (sayContent.startsWith('Checkbox(')) {
          type = 'checkbox';
          const titleMatch = sayContent.match(/title=["']([^"']*)["']/);
          content = titleMatch ? titleMatch[1] : 'Check all that apply:';
          options = declaredChoices.length
            ? declaredChoices
            : parseOptionsFromSay(sayContent, [
                { label: 'Option A', payload: 'a' },
                { label: 'Option B', payload: 'b' },
              ]);
        } else if (sayContent.startsWith('Dropdown(')) {
          type = 'dropdown';
          const titleMatch = sayContent.match(/title=["']([^"']*)["']/);
          content = titleMatch ? titleMatch[1] : 'Select one:';
          options = declaredChoices.length
            ? declaredChoices
            : parseOptionsFromSay(sayContent, [
                { label: 'Option A', payload: 'a' },
                { label: 'Option B', payload: 'b' },
              ]);
        } else if (sayContent.startsWith('LaTeX(')) {
          type = 'latex';
          const match = sayContent.match(/["']([^"']*)["']/);
          content = match ? match[1] : '\\(x^2 + y^2 = z^2\\)';
          const ttsMatch = sayContent.match(/tts=["']([^"']*)["']/);
          if (ttsMatch) extraProps.tts = ttsMatch[1];
        } else if (sayContent.startsWith('Signature(')) {
          type = 'signature';
          const match = sayContent.match(/["']([^"']*)["']/);
          content = match ? match[1] : 'Please sign here';
          const descMatch = sayContent.match(/description=["']([^"']*)["']/);
          if (descMatch) extraProps.description = descMatch[1];
          const labelMatch = sayContent.match(/submit_label=["']([^"']*)["']/);
          if (labelMatch) extraProps.submit_label = labelMatch[1];
        } else if (sayContent.startsWith('UserInput(')) {
          type = 'userinput';
          const match = sayContent.match(/UserInput\((true|false)\)/);
          extraProps.enabled = match ? match[1] === 'true' : true;
          content = extraProps.enabled ? 'Enabled' : 'Disabled';
        } else if (
          sayContent.startsWith('HTTP') ||
          sayContent.startsWith('API')
        ) {
          type = 'api';
          isApi = true;
          Object.assign(extraProps, parseHttpCall(sayContent));
          content = 'Call API';
        } else if (sayContent.startsWith('OAuthRequest(')) {
          type = 'oauth';
          const match = sayContent.match(/provider=["']([^"']*)["']/);
          extraProps.provider = match ? match[1] : 'google';
          content = 'Authenticate user';
        } else if (isApi || type === 'assign') {
          // An API or Assign node's own trailing `say "..."` line matches no
          // component pattern either, so without this guard it would fall
          // through and clobber the node's type back to 'say' - discarding
          // the classification, and with it any hope of reattaching the
          // node's branches below. That line IS the node's message, so keep
          // it as the content rather than dropping it.
          content = sayContent.replace(/^"|"$/g, '');
        } else {
          type = 'say';
          content = sayContent.replace(/^"|"$/g, '');
        }
        return;
      }
      if (line.startsWith('do ') && line.includes('HTTP')) {
        type = 'api';
        isApi = true;
        Object.assign(extraProps, parseHttpCall(line));
        content = 'Call API';
        return;
      }
      const chainedCall = line.match(
        /^do\s+[A-Za-z_][A-Za-z0-9_]*\s*=\s*response\.(get|post|put|patch|delete)\s*\(/i
      );
      if (isApi && chainedCall) {
        extraProps.apiMethod = chainedCall[1].toUpperCase();
        const body = extractCallArgument(line, chainedCall[1].toLowerCase());
        if (body) extraProps.apiBody = body;
        return;
      }
      if (
        isApi &&
        /^do\s+[A-Za-z_][A-Za-z0-9_]*\s*=\s*response\.send\s*\(\s*\)\s*$/.test(
          line
        )
      ) {
        return;
      }
      if (line.startsWith('do ') && line.includes('Button')) {
        const match = line.match(
          new RegExp(
            `^do\\s+(${STEP_NAME})\\s*=\\s*Button\\(["']([^"']*)["'](?:,\\s*payload=["']([^"']*)["'])?\\)`
          )
        );
        if (match) {
          declaredChoices.push({
            varName: match[1],
            label: match[2],
            payload: match[3] || '',
          });
        }
        return;
      }
      if (isApi && line.includes('if (response.')) {
        // Generated status/header rules read response.get_info().status and
        // response.get_info().headers. The field is the name after get_info().
        // contains/starts_with/ends_with wrap the value in parentheses.
        const ruleMatch = line.match(
          /if\s*\(\s*response\.(?:get_info\(\)\.)?(\w+)\s*(==|!=|>=|<=|>|<|\.contains|\.starts_with|\.ends_with)\s*\(?\s*["']?([^"'\s)]*)["']?\s*\)?\s*\)/
        );
        if (ruleMatch) {
          const field = ruleMatch[1];
          const operator = ruleMatch[2].replace('.', '');
          const value = ruleMatch[3];
          let op = 'equals';
          if (operator === '!=') op = 'not_equals';
          else if (operator === '>') op = 'gt';
          else if (operator === '<') op = 'lt';
          else if (operator === 'contains') op = 'contains';
          else if (operator === 'starts_with') op = 'starts';
          else if (operator === 'ends_with') op = 'ends';
          responseRules.push({
            label: `${field} ${op} ${value}`,
            field,
            operator: op,
            value,
          });
        }
      } else {
        // A statement we could not turn into a node. Saving would drop it.
        lossy = true;
      }
    });

    // A block that is only `hold` (plus an optional goto) is a Hold node.
    // Every block defaults to `say`, and an empty say is filled with
    // "Message", so a hold-only block was reimported as a Say wired into a
    // second Hold. Saving wrote that Say back, and the next open added
    // another one in front of the Hold.
    if (hasHold && type === 'say' && !String(content).trim()) {
      type = 'hold';
      content = 'Wait for user input';
    }

    const x = 100 + (xOffset % 3) * 320;
    const y = 100 + Math.floor(xOffset / 3) * 180;
    xOffset += 1;
    if (eventMemory) extraProps.eventMemory = eventMemory;

    const keywordEntries = gotoEntries.filter(entry => entry.keywordIndex);
    const otherEntries = gotoEntries.filter(entry => !entry.keywordIndex);

    // Keyword checks run at the top of the step, then the step holds and
    // calls the AI. Those have to stay separate nodes so the reply happens
    // before the loop re-enters the keywords.
    if (keywordRules.length && type !== 'question' && type !== 'quickreply') {
      const router = place(
        {
          type: 'router',
          keywords: keywordRules,
          content: keywordRules.map(rule => rule.match).join(' · '),
        },
        x,
        y
      );
      let tail = router;
      if (hasHold || isApi) {
        router.gotoEntries = keywordEntries;
        if (hasHold) {
          const holdNode = place(
            {
              type: 'hold',
              icon: 'i-lucide-circle-pause',
              content: 'Wait for user input',
            },
            x + 320,
            y
          );
          connections.push({
            fromId: tail.id,
            toId: holdNode.id,
            fromPortIdx: 0,
          });
          tail = holdNode;
        }
        if (isApi) {
          const apiNode = place(
            { type: 'api', content: '', ...extraProps },
            x + (hasHold ? 640 : 320),
            y
          );
          connections.push({
            fromId: tail.id,
            toId: apiNode.id,
            fromPortIdx: 0,
          });
          tail = apiNode;
        }
        if (otherEntries.length) tail.gotoEntries = otherEntries;
      } else if (gotoEntries.length) {
        router.gotoEntries = gotoEntries;
      }
      nodeMap[label] = router;
      return;
    }

    // `hold` then the HTTP call is two waits when the previous branch also
    // held. The hold is the step the rest of the flow jumps to.
    if (sawHoldBeforeBody && isApi) {
      const holdNode = place(
        {
          type: 'hold',
          icon: 'i-lucide-circle-pause',
          content: 'Wait for user input',
        },
        x,
        y
      );
      const apiNode = place(
        { type: 'api', content: '', ...extraProps },
        x + 320,
        y
      );
      connections.push({
        fromId: holdNode.id,
        toId: apiNode.id,
        fromPortIdx: 0,
      });
      if (gotoEntries.length) apiNode.gotoEntries = gotoEntries;
      nodeMap[label] = holdNode;
      return;
    }

    const nodeData = {
      id: nextId,
      type,
      label:
        type === 'start'
          ? 'Start'
          : type.charAt(0).toUpperCase() + type.slice(1),
      x,
      y,
      content:
        extraProps.callStyle === 'http.post' || MEDIA_TYPES.includes(type)
          ? content
          : content || 'Message',
      ...extraProps,
    };
    nextId += 1;

    if (type === 'question') {
      nodeData.buttons = buttons.length
        ? buttons
        : [
            { label: 'Option 1', payload: 'btn1' },
            { label: 'Option 2', payload: 'btn2' },
          ];
    }
    if (type === 'quickreply') {
      nodeData.buttons = buttons.length
        ? buttons
        : [
            { label: 'Yes', payload: 'yes' },
            { label: 'No', payload: 'no' },
          ];
    }
    if (['radio', 'multiselect', 'checkbox', 'dropdown'].includes(type)) {
      nodeData.options = options.length
        ? options
        : [
            { label: 'Option 1', payload: 'opt1' },
            { label: 'Option 2', payload: 'opt2' },
          ];
    }
    if (type === 'carousel') {
      nodeData.cards = cards.length
        ? cards
        : [
            {
              title: 'Card 1',
              subtitle: 'Sub',
              image_url: 'https://via.placeholder.com/150',
              buttons: [{ label: 'Action', payload: 'act' }],
            },
          ];
    }
    if (type === 'api' && responseRules.length > 0) {
      nodeData.responseRules = responseRules;
    }
    if (type === 'assign') {
      nodeData.assignType = extraProps.assignType || 'team';
      nodeData.teamId = extraProps.teamId || '';
      nodeData.agentId = extraProps.agentId || '';
    }

    if (
      hasHold &&
      type !== 'hold' &&
      ![
        'question',
        'quickreply',
        'radio',
        'multiselect',
        'checkbox',
        'dropdown',
        'calendar',
        'input',
        'signature',
        'oauth',
        'carousel',
      ].includes(type)
    ) {
      const holdNode = {
        id: nextId,
        type: 'hold',
        label: 'Hold',
        icon: 'i-lucide-circle-pause',
        content: 'Wait for user input',
        x: x + 350,
        y,
      };
      nextId += 1;
      nodes.push(holdNode);
      connections.push({
        fromId: nodeData.id,
        toId: holdNode.id,
        fromPortIdx: 0,
      });
      nodeData.holdNodeId = holdNode.id;
    }

    if (label === 'start') {
      nodeData.type = 'start';
      nodeData.label = 'Start';
      // A start step that only jumps should not grow a placeholder say.
      if (type === 'say' && !String(content).trim()) nodeData.content = '';
      nodes = nodes.filter(n => n.type !== 'start');
    }

    nodes.push(nodeData);
    nodeMap[label] = nodeData;
    if (gotoEntries.length) {
      nodeData.gotoEntries = gotoEntries;
    }
  });

  nodes.forEach(node => {
    if (node.gotoEntries) {
      node.gotoEntries.forEach(entry => {
        const target = entry.targetLabel ? nodeMap[entry.targetLabel] : null;
        if (!target && !(entry.inserts || []).length) return;
        // Resolve which port this goto belongs to. API rule branches are
        // matched by the order they were declared (mirrors how the
        // generator numbers rule ports); button/option branches carry the
        // real port index as the trailing number in their match variable
        // name (btn_<id>_<i>, opt_<id>_<i>) regardless of what node id was
        // baked into that name originally; carousel match variables encode
        // both the card and button index (card<i>_btn<j>). Anything else is
        // a bare, unconditional goto from the node's single default port.
        let portIdx = 0;
        if (entry.portIdx != null) {
          portIdx = entry.portIdx;
        } else if (entry.buttonIndex != null) {
          portIdx = entry.buttonIndex;
        } else if (entry.keywordIndex != null) {
          portIdx = entry.keywordIndex;
        } else if (entry.apiRuleOrder) {
          portIdx = entry.apiRuleOrder;
        } else if (entry.matchVar) {
          if (node.type === 'carousel') {
            const cardBtnMatch = entry.matchVar.match(/^card(\d+)_btn(\d+)$/);
            if (cardBtnMatch) {
              portIdx = Number(cardBtnMatch[1]) * 10 + Number(cardBtnMatch[2]);
            }
          } else {
            const trailingIdx = entry.matchVar.match(/_(\d+)$/);
            if (trailingIdx) portIdx = Number(trailingIdx[1]);
          }
        }
        const inserts = entry.inserts || [];
        let fromId = node.holdNodeId || node.id;
        let fromPort = portIdx;
        inserts.forEach((insert, index) => {
          const created = place(
            {
              ...insert,
              content:
                insert.content ||
                (insert.type === 'hold' ? 'Wait for user input' : ''),
              icon:
                insert.type === 'hold' ? 'i-lucide-circle-pause' : insert.icon,
            },
            (node.x || 100) + 280,
            (node.y || 100) + index * 140
          );
          connections.push({
            fromId,
            toId: created.id,
            fromPortIdx: fromPort,
          });
          fromId = created.id;
          fromPort = 0;
        });
        if (!target) return;
        connections.push({
          fromId,
          toId: target.id,
          fromPortIdx: fromPort,
        });
      });
    }
    delete node.gotoEntries;
    delete node.holdNodeId;
  });

  if (!nodes.some(n => n.type === 'start')) {
    const startNode = {
      ...createNodeFromTemplate('start'),
      id: nextId,
      x: 100,
      y: 100,
    };
    nextId += 1;
    nodes.unshift(startNode);
    const firstNode = nodes.find(n => n.type !== 'start');
    if (firstNode) {
      connections.push({
        fromId: startNode.id,
        toId: firstNode.id,
        fromPortIdx: 0,
      });
    }
  }

  return { nodes, connections, nextId, lossy };
}

export function useCsmlScriptImport() {
  return { buildFromScript };
}
